/* ═══════════════════════════════════════════════════════════════════════
   OTODOR Gate Price Builder — the calculator behind otodor-gate-builder.

   There is only ONE sum, and it is the briefs' (Pellidium §12, Blanco §11):

     total = unit + rack? + sensors? + light? + care? + installation
     rack  = ceil(gate panel length) metres, never fewer than `rackMin`,
             multiplied by the per-metre price — SLIDING ONLY
     saving = Σ MRP − Σ OTODOR price, across the unit, rack, sensors and
              light only — installation and Care+ have no MRP, so they are
              not part of it

   Step 2 differs by product and the difference is in `cfg.size.mode`:

     'rack'   a sliding gate's panel length PRICES the gear rack, so the
              answer is required and the send actions block without it — a
              quote with no rack in it is simply wrong.
     'limit'  a swing gate's leaf width prices NOTHING. It checks the leaf
              against the arm's stroke and, when it is too wide, WARNS AND
              LETS THE CUSTOMER THROUGH. Blocking here would send them to a
              competitor with a worse product and a friendlier form; the
              warning keeps both their price and our lead.

   Nothing else is calculated anywhere. Every figure comes from the config
   block the section prints, which comes from the price registry, which is
   the single place a price is edited.

   Per-instance: two builders on one page keep separate state. No globals
   beyond the custom element, no dependencies, no build step.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var RUPEE = '₹';

  /* Indian digit grouping — 1,25,000 rather than 125,000. Intl does this
     correctly for en-IN; the manual path is for engines that do not. */
  function money(n) {
    var v = Math.max(0, Math.round(Number(n) || 0));
    try {
      return RUPEE + v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    } catch (e) {
      var s = String(v);
      if (s.length <= 3) return RUPEE + s;
      var head = s.slice(0, -3);
      var tail = s.slice(-3);
      return RUPEE + head.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + tail;
    }
  }

  function h(el, name) {
    return el.querySelector('[data-gb="' + name + '"]');
  }

  function all(el, name) {
    return Array.prototype.slice.call(el.querySelectorAll('[data-gb="' + name + '"]'));
  }

  function GateBuilder(root) {
    this.root = root;
    var raw = root.querySelector('[data-gb-config]');
    if (!raw) return;

    try {
      this.cfg = JSON.parse(raw.textContent);
    } catch (e) {
      return;
    }
    if (!this.cfg || !this.cfg.motors || !this.cfg.motors.length) return;

    this.state = {
      motor: this.cfg.motors[0].id,
      length: null,
      sensors: this.cfg.defaults.sensors,
      light: this.cfg.defaults.light,
      care: this.cfg.defaults.care
    };

    this.readUrl();
    this.bind();
    this.render();
    this.ready = true;
  }

  /* A model card anywhere on the page can deep-link in with its motor
     already chosen — ?motor=550w, or #anchor from the models section. */
  GateBuilder.prototype.readUrl = function () {
    var want;
    try {
      want = new URLSearchParams(window.location.search).get('motor');
    } catch (e) {
      want = null;
    }
    if (!want) return;
    want = String(want).toLowerCase();
    for (var i = 0; i < this.cfg.motors.length; i++) {
      if (this.cfg.motors[i].id === want) {
        this.state.motor = want;
        return;
      }
    }
  };

  GateBuilder.prototype.select = function (id) {
    for (var i = 0; i < this.cfg.motors.length; i++) {
      if (this.cfg.motors[i].id === id) {
        this.state.motor = id;
        this.render();
        return true;
      }
    }
    return false;
  };

  GateBuilder.prototype.bind = function () {
    var self = this;

    all(this.root, 'motor').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.select(btn.getAttribute('data-motor'));
      });
    });

    var len = h(this.root, 'length');
    if (len) {
      len.addEventListener('input', function () {
        var v = parseFloat(len.value);
        self.state.length = isFinite(v) && v > 0 ? v : null;
        self.render();
      });
    }

    ['sensors', 'light', 'care'].forEach(function (key) {
      all(self.root, key + '-choice').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.state[key] = btn.getAttribute('data-value') === 'yes';
          self.render();
        });
      });
    });

    var email = h(this.root, 'email-submit');
    if (email) {
      email.addEventListener('click', function (ev) {
        if (!self.validate()) ev.preventDefault();
      });
    }

    var wa = h(this.root, 'wa');
    if (wa) {
      wa.addEventListener('click', function (ev) {
        if (!self.validate()) {
          ev.preventDefault();
          return;
        }
        wa.setAttribute('href', self.whatsappHref());
      });
    }

    /* The fixed bar is only wanted once the builder is actually on screen —
       a total pinned to the bottom of a page the reader has not reached yet
       is an advert, not a summary. */
    this.observeBar();
  };

  GateBuilder.prototype.observeBar = function () {
    var bar = h(this.root, 'bar');
    if (!bar || !('IntersectionObserver' in window)) return;
    var self = this;

    var io = new IntersectionObserver(function (entries) {
      var on = entries[0].isIntersecting;
      bar.classList.toggle('otodor-gb-bar--on', on);
      document.body.classList.toggle('otodor-gate-bar-on', on);
      if (on) {
        document.documentElement.style.setProperty('--otodor-gate-bar-h', bar.offsetHeight + 'px');
      } else {
        document.documentElement.style.removeProperty('--otodor-gate-bar-h');
      }
      self.barOn = on;
    }, { rootMargin: '0px 0px -35% 0px' });

    io.observe(this.root.querySelector('[data-gb="steps"]') || this.root);
  };

  /* ── The one sum ─────────────────────────────────────────────────── */
  GateBuilder.prototype.motor = function () {
    for (var i = 0; i < this.cfg.motors.length; i++) {
      if (this.cfg.motors[i].id === this.state.motor) return this.cfg.motors[i];
    }
    return this.cfg.motors[0];
  };

  GateBuilder.prototype.mode = function () {
    return (this.cfg.size && this.cfg.size.mode) || 'rack';
  };

  GateBuilder.prototype.rackMetres = function () {
    if (this.mode() !== 'rack') return 0;
    if (this.state.length === null) return 0;
    return Math.max(Math.ceil(this.state.length), this.cfg.rack.min);
  };

  /* null = nothing typed, true = within the limit, false = over it. */
  GateBuilder.prototype.withinLimit = function () {
    if (this.mode() !== 'limit') return null;
    if (this.state.length === null) return null;
    var max = Number(this.cfg.size.max) || 0;
    if (!max) return null;
    return this.state.length <= max;
  };

  GateBuilder.prototype.quote = function () {
    var m = this.motor();
    var metres = this.rackMetres();
    var lines = [];

    lines.push({ key: 'motor', label: m.name + ' — ' + m.weight, qty: '1', amt: m.price, mrp: m.mrp });

    /* A swing kit has no rack at all — not a rack costing nothing. A zero
       line in an itemised quote invites the question "what is that?", and
       the answer is that it should not have been printed. */
    if (this.mode() === 'rack') {
      if (metres > 0) {
        lines.push({
          key: 'rack',
          label: this.cfg.rack.label,
          qty: metres + (metres === 1 ? ' metre' : ' metres'),
          amt: metres * this.cfg.rack.price,
          mrp: metres * this.cfg.rack.mrp
        });
      } else {
        lines.push({ key: 'rack', label: this.cfg.rack.label, qty: '', amt: null, mrp: 0, pending: true });
      }
    }

    if (this.state.sensors) {
      lines.push({ key: 'sensors', label: this.cfg.sensors.label, qty: '1 pair', amt: this.cfg.sensors.price, mrp: this.cfg.sensors.mrp });
    }
    if (this.state.light) {
      lines.push({ key: 'light', label: this.cfg.light.label, qty: '1', amt: this.cfg.light.price, mrp: this.cfg.light.mrp });
    }
    if (this.state.care) {
      lines.push({ key: 'care', label: this.cfg.care.label, qty: '1', amt: this.cfg.care.price, mrp: 0 });
    }

    lines.push({ key: 'install', label: this.cfg.install.label, qty: '1', amt: this.cfg.install.price, mrp: 0 });

    var total = 0;
    var saving = 0;
    lines.forEach(function (l) {
      if (l.amt === null) return;
      total += l.amt;
      /* Only items with a real MRP contribute to the saving. Installation
         and Care+ have none, so they are outside it — quoting a "saving"
         against a price that was never published would be a lie. */
      if (l.mrp > 0) saving += l.mrp - l.amt;
    });

    return { lines: lines, total: total, saving: saving, metres: metres };
  };

  /* ── Rendering ───────────────────────────────────────────────────── */
  GateBuilder.prototype.render = function () {
    var self = this;
    var q = this.quote();

    all(this.root, 'motor').forEach(function (btn) {
      var on = btn.getAttribute('data-motor') === self.state.motor;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    ['sensors', 'light', 'care'].forEach(function (key) {
      all(self.root, key + '-choice').forEach(function (btn) {
        var on = (btn.getAttribute('data-value') === 'yes') === self.state[key];
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var conseq = h(self.root, key + '-consequence');
      if (conseq) conseq.hidden = self.state[key];
    });

    var answer = h(this.root, 'answer');
    var warn = h(this.root, 'warn');
    if (answer) answer.hidden = true;
    if (warn) warn.hidden = true;

    if (this.mode() === 'rack') {
      if (answer && q.metres > 0) {
        answer.hidden = false;
        answer.textContent =
          'A ' + this.state.length + ' m gate panel needs ' + q.metres +
          (q.metres === 1 ? ' metre' : ' metres') + ' of gear rack — ' +
          money(q.metres * this.cfg.rack.price) + '.';
      }
    } else {
      var ok = this.withinLimit();
      if (ok === true && answer) {
        answer.hidden = false;
        answer.textContent = this.fill(this.cfg.size.okText);
      } else if (ok === false && warn) {
        warn.hidden = false;
        warn.textContent = this.fill(this.cfg.size.overText);
      }
    }

    var list = h(this.root, 'lines');
    if (list) {
      list.innerHTML = '';
      q.lines.forEach(function (l) {
        var li = document.createElement('li');
        li.className = 'otodor-gb-sum__line';

        var label = document.createElement('span');
        label.className = 'otodor-gb-sum__label';
        label.textContent = l.label;
        if (l.qty) {
          var qty = document.createElement('span');
          qty.className = 'otodor-gb-sum__qty';
          qty.textContent = l.qty;
          label.appendChild(qty);
        }

        var amt = document.createElement('span');
        amt.className = 'otodor-gb-sum__amt';
        amt.textContent = l.pending ? self.cfg.text.rackPending : money(l.amt);

        li.appendChild(label);
        li.appendChild(amt);
        list.appendChild(li);
      });
    }

    var total = h(this.root, 'total');
    if (total) total.textContent = money(q.total);

    var barAmt = h(this.root, 'bar-total');
    if (barAmt) barAmt.textContent = money(q.total);

    var saving = h(this.root, 'saving');
    var savingAmt = h(this.root, 'saving-amt');
    if (saving && savingAmt) {
      saving.hidden = q.saving <= 0;
      savingAmt.textContent = money(q.saving);
    }

    var quoteField = h(this.root, 'quote-field');
    if (quoteField) quoteField.value = this.plainQuote(q);

    var waLink = h(this.root, 'wa');
    if (waLink) waLink.setAttribute('href', this.whatsappHref());

    var barLink = h(this.root, 'bar-cta');
    if (barLink) barLink.setAttribute('href', this.whatsappHref());
  };

  /* {size} and {max} kept as placeholders rather than string concatenation,
     so a merchant can rewrite the sentence around them in any order — or in
     another language — without touching this file. */
  GateBuilder.prototype.fill = function (tpl) {
    return String(tpl || '')
      .replace(/\{size\}/g, this.state.length === null ? '' : String(this.state.length))
      .replace(/\{max\}/g, String(this.cfg.size.max));
  };

  /* ── Handoff ─────────────────────────────────────────────────────── */
  GateBuilder.prototype.field = function (name) {
    var el = h(this.root, name);
    return el ? String(el.value || '').trim() : '';
  };

  GateBuilder.prototype.plainQuote = function (q) {
    var self = this;
    var out = [this.cfg.text.messageIntro];
    out.push('');
    var sizeLabel = (this.cfg.size && this.cfg.size.label) || 'Gate panel length';
    out.push(sizeLabel + ': ' + (this.state.length === null ? 'not given' : this.state.length + ' m'));
    if (this.withinLimit() === false) {
      out.push('(Wider than the ' + this.cfg.size.max + ' m this arm can push — please advise.)');
    }
    out.push('');
    q.lines.forEach(function (l) {
      out.push('• ' + l.label + (l.qty ? ' (' + l.qty + ')' : '') + ': ' + (l.pending ? self.cfg.text.rackPending : money(l.amt)));
    });
    out.push('');
    out.push('Total: ' + money(q.total));
    if (q.saving > 0) out.push('Saving against MRP: ' + money(q.saving));

    var name = this.field('name');
    var mobile = this.field('mobile');
    var pincode = this.field('pincode');
    if (name || mobile || pincode) {
      out.push('');
      if (name) out.push('Name: ' + name);
      if (mobile) out.push('Mobile: ' + mobile);
      if (pincode) out.push('Pincode: ' + pincode);
    }
    out.push('');
    out.push(this.cfg.text.messageOutro);
    return out.join('\n');
  };

  GateBuilder.prototype.whatsappHref = function () {
    if (!this.cfg.whatsapp) return '#';
    /* Encode the MESSAGE, not the whole URL — url_encode over a built href
       escapes the ? and & that make it a query string. */
    return 'https://wa.me/' + this.cfg.whatsapp + '?text=' + encodeURIComponent(this.plainQuote(this.quote()));
  };

  /* In `rack` mode the length is the one answer the quote cannot be right
     without, so it is the one thing that blocks. In `limit` mode nothing
     blocks at all: the price is already complete after step 1, and the
     brief is explicit that an over-size leaf must warn and let the customer
     through. */
  GateBuilder.prototype.validate = function () {
    var status = h(this.root, 'status');
    if (this.mode() !== 'rack' || this.state.length !== null) {
      if (status) {
        status.textContent = '';
        status.className = 'otodor-gb-status';
      }
      return true;
    }
    if (status) {
      status.textContent = this.cfg.text.needLength;
      status.className = 'otodor-gb-status otodor-gb-status--err';
    }
    var len = h(this.root, 'length');
    if (len) len.focus();
    return false;
  };

  var instances = [];

  function init(scope) {
    Array.prototype.slice
      .call((scope || document).querySelectorAll('[data-otodor-gate-builder]'))
      .forEach(function (el) {
        if (el.__gbInit) return;
        el.__gbInit = true;
        var gb = new GateBuilder(el);
        if (gb.ready) instances.push(gb);
      });
  }

  /* A model card anywhere on the page — the three-card section above, a
     comparison table, a link in the FAQ — can carry the reader into the
     builder with that motor already chosen. The link is a real anchor with
     a real href, so it still works with this script blocked; the handler
     only adds the selection on the way past.

     Delegated from the document rather than bound per card, because the
     cards live in a DIFFERENT section and the theme editor can re-render
     either one of the two independently. */
  document.addEventListener('click', function (ev) {
    var jump = ev.target.closest ? ev.target.closest('[data-gb-jump]') : null;
    if (!jump) return;
    var want = jump.getAttribute('data-gb-jump');
    if (!want) return;

    var target = jump.getAttribute('href') || '';
    for (var i = 0; i < instances.length; i++) {
      /* With more than one builder on the page, honour the one the link
         actually points at. */
      if (target && target.charAt(0) === '#' && '#' + instances[i].root.id !== target) continue;
      if (instances[i].select(want)) break;
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Theme editor: a section re-render replaces the DOM under us. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
